import React, { useEffect, useRef, useState } from 'react';
import { ReactSVG } from 'react-svg';
import moment from 'moment';
import { useIntl } from 'react-intl';
import messages from '../messages';

import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, CircularProgress, IconButton, Modal, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close, CloudUpload } from '@mui/icons-material';
import { useStyles } from './styles';
import { getMainStyles } from '../../../../styles/styles';
import { IconSvg } from '../../../../utils/globalIcons';

import { setExtractionDocumentTypesState, setExtractionMainDocumentTypesState } from '../../../../Redux/slice/dataSlice';
import { getAllMainExtractionDocumentTypes, getAllCustomExtractionDocumentTypes, getAllExtractionDocumentTypes, extractionFileSave } from '../../../../utils/ApiFunctions/ExtractionData';
import { getMainExtractionSchema, getCustomExtractionSchema } from '../../../../utils/ApiFunctions/ExtractionSchemas';

import { tostAlert } from '../../../../utils/AlertToast';
import TemplateExtractionApi from '../../../../Api/Services/AppflyteBackend/TemplateExtractionApi';
import LoadBar from '../../../../utils/LoadBar';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../../../context/AppContext';
import { getAgentApiToken } from '../../../../utils/GetAccountDetails';

import ExtractionTraining from '../../../../components/ExtractionTraining';
import SchemaBuilder from '../../../../components/SchemaBuilder';
import handleFileUpload from '../../../../utils/fileUpload';

const schema_builder_plugin_url = window.__ENV__.EXTRACTION_SCHEMA_BUILDER_PLUGIN_URL;

function AddDocumentTypes() {
    const theme = useTheme();
    const styles = useStyles(theme);
    const mainStyles = getMainStyles(theme);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const intl = useIntl();
    const { selectedProject, handleExtractionSteps } = useAppContext();

    const [loading, setLoading] = useState(false);
    const [creatingDocType, setCreatingDocType] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    const doc_types_details = {
        all: [],
        logisticsAndShippingTypes: [],
        certificatesTypes: [],
        tradeDocumentTypes: [],
        coaTypes: [],
        customsDocument: [],
        procurementDocument: [],
        incomeDocument: [],
        customTypes: []
    }
    const [extractionDocumentTypes, setExtractionDocumentTypes] = useState(doc_types_details);

    const MODES = {
        DOCUMENT: "document",
        TRAIN: "train",
        SCHEMA: "schema-builder"
    }
    const [previewMode, setPreviewMode] = useState(MODES.DOCUMENT);
    const [documentTypeId, setDocumentTypeId] = useState(null);
    const [documentType, setDocumentType] = useState(null);
    const [agentTokenId, setAgentTokenId] = useState(null);
    const [fileId, setFileId] = useState(null);
    const prevModeRef = useRef(previewMode);

    const extraction_main_document_types = useSelector(state => state.all_data.extraction_main_document_types)
    const modalDetails = { modalstatus: false, document_name: null, document_type_name: '', master_document_type: '', document_type_category: '' };
    const [modalData, setModalData] = useState(modalDetails);
    const [selectedFiles, setSelectedFiles] = useState([]);

    useEffect(() => {
        getAllData();
        //eslint-disable-next-line
    }, []);

    useEffect(() => {
        const prevMode = prevModeRef.current;
        if (prevMode === MODES.SCHEMA && previewMode === MODES.DOCUMENT) {
            getAllData();
        }
        prevModeRef.current = previewMode;
        //eslint-disable-next-line
    }, [previewMode]);

    useEffect(() => {
        const errors = { ...formErrors };
        if (formErrors?.document_type_name && modalData?.document_type_name.trim()) { delete errors?.document_type_name }
        if (JSON.stringify(errors) !== JSON.stringify(formErrors)) { setFormErrors(errors) }
    }, [modalData, formErrors]);

    const getAllData = async () => {
        setLoading(true)
        try {

            const [main_docs, custom_docs] = await Promise.all([getMainDocumentTypes(), getCustomDocumentTypes()])

            const coa_types = (main_docs || [])?.filter(item => item?.payload?.category_type === "coa") ?? [];
            const certificate_types = (main_docs || [])?.filter(item => item?.payload?.category_type === "certificates") ?? [];
            const logistics_and_shipping_types = (main_docs || [])?.filter(item => item?.payload?.category_type === "logistics_and_shipping") ?? [];
            const trade_document_types = (main_docs || [])?.filter(item => item?.payload?.category_type === "trade_document") ?? [];
            const customs_document_types = (main_docs || [])?.filter(item => item?.payload?.category_type === "customs_document") ?? [];
            const procurement_types = (main_docs || [])?.filter(item => item?.payload?.category_type === "procurement") ?? [];
            const income_types = (main_docs || [])?.filter(item => item?.payload?.category_type === "income") ?? [];
            const custom_types = (custom_docs || [])?.filter(item => item?.payload?.category_type === "custom") ?? [];

            setExtractionDocumentTypes(prev => ({
                ...prev,
                all: [...main_docs, ...custom_docs],
                coaTypes: coa_types,
                certificatesTypes: certificate_types,
                logisticsAndShippingTypes: logistics_and_shipping_types,
                tradeDocumentTypes: trade_document_types,
                customsDocument: customs_document_types,
                procurementDocument: procurement_types,
                incomeDocument: income_types,
                customTypes: custom_types
            }));

        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    const getMainDocumentTypes = async () => {
        try {
            if (extraction_main_document_types?.length > 0) {
                return extraction_main_document_types
            } else {
                const response = await getAllMainExtractionDocumentTypes();
                dispatch(setExtractionMainDocumentTypesState(response))
                return response
            }
        } catch (error) {
            console.log(error);
            return [];
        }
    }

    const getCustomDocumentTypes = async () => {
        try {
            const response = await getAllCustomExtractionDocumentTypes();
            return response
        } catch (error) {
            console.log(error);
            return [];
        }
    }

    const getDocumentTypes = async () => {
        try {
            const project_id = selectedProject?.payload?.__auto_id__
            const response = await getAllExtractionDocumentTypes(project_id);
            dispatch(setExtractionDocumentTypesState(response))
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    const handleModalOpen = (item) => {
        const document_type_category = item?.payload?.category_type ?? null;
        const document_type = item?.payload?.document_type ?? null;
        const document_name = item?.payload?.name ?? null;
        setModalData({
            ...modalData,
            modalstatus: true,
            document_name: document_name,
            master_document_type: document_type,
            document_type_category: document_type_category
        });
    }

    const handleModalClose = () => {
        setModalData(modalDetails);
    }

    const handleFileDrop = async (event) => {
        try {
            event.preventDefault();
            event.stopPropagation();
            const files = Array.from(event.dataTransfer.files);
            event.target.value = null;
            setSelectedFiles(files);
        } catch (error) {
            console.log(error)
        }
    };

    const handleFileChange = async (event) => {
        try {
            event.preventDefault();
            event.stopPropagation();
            const files = Array.from(event.target.files);
            event.target.value = null;
            setSelectedFiles(files);
        } catch (error) {
            console.log(error)
        }
    };

    const handleFileRemove = (index) => {
        const updatedFiles = [...selectedFiles];
        updatedFiles.splice(index, 1);
        setSelectedFiles(updatedFiles);
    }

    const handleDocumentTypeCreation = async () => {
        try {
            const reqObj = {
                collection_item: {
                    "name": modalData.document_type_name,
                    "created_on": moment().format("YYYY-MM-DD HH:mm:ss"),
                    "document_type": modalData.document_type_name,
                    "master_document_type": modalData.master_document_type,
                    "document_type_category": modalData.document_type_category,
                    "project_id": selectedProject?.payload?.__auto_id__,
                    "is_main": false
                }
            }
            const response = await TemplateExtractionApi.createExtractionDocumentTypes(JSON.stringify(reqObj));
            if (response?.status === 200) {
                const responseData = response?.data ?? null;
                getDocumentTypes();
                return responseData
            }
            return null
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    const handleExtaractionSchemaCreation = async (document_type, extraction_schema) => {
        try {
            const reqObj = {
                collection_item: {
                    "settings": extraction_schema,
                    "project_id": selectedProject?.payload?.__auto_id__,
                    "created_on": moment().format("YYYY-MM-DD HH:mm:ss"),
                    "document_type": document_type
                }
            }
            const response = await TemplateExtractionApi.createExtractionSchema(JSON.stringify(reqObj));
            if (response?.status === 200) {
                const responseData = response?.data?.id ?? null;
                return responseData
            }
            return null
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    const checkNameExistence = async () => {
        try {
            const { status, data } = await TemplateExtractionApi.checkNameExistence(modalData.document_type_name);
            if (status !== 200) return true;

            const collections = data?.published_collections_detail || [];
            if (collections.length === 0) return false;

            const existingData = collections.flatMap(c => data[c.id] || []);
            return existingData.some(item => item?.payload?.project_id === selectedProject?.payload?.__auto_id__);
        } catch (err) {
            console.log(err);
            return true;
        }
    };

    const handleUploadTrain = async () => {
        setCreatingDocType(true);
        try {

            const errors = {};

            if (!modalData.document_type_name.trim()) { errors.document_type_name = 'This Field is required' }
            if (Object.keys(errors).length > 0) {
                setFormErrors(errors);
                return
            }

            const name_exist = await checkNameExistence()
            if (name_exist) {
                tostAlert(intl.formatMessage(messages.doctype_exists), 'warning')
                return
            }

            if (selectedFiles?.length === 0) {
                tostAlert(intl.formatMessage(messages.file_selection_error), 'warning')
                return
            }

            const document_data = await handleDocumentTypeCreation();
            if (!document_data) {
                tostAlert(intl.formatMessage(messages.doctype_add_error), "error");
            }

            let extarction_schema = null;
            if (modalData.document_type_category === "custom") {
                const schema_data = await getCustomExtractionSchema(modalData.master_document_type);
                extarction_schema = schema_data?.at(-1)?.payload?.settings ?? null;
            } else {
                const schema_data = await getMainExtractionSchema(modalData.master_document_type);
                extarction_schema = schema_data?.at(-1)?.payload?.settings ?? null;
            }

            const doc_type_id = document_data?.payload?.__auto_id__ ?? null;
            const doc_type = document_data?.payload?.master_document_type ?? null;
            await handleExtaractionSchemaCreation(doc_type_id, extarction_schema)

            setDocumentTypeId(doc_type_id)
            setDocumentType(doc_type)

            const file_name = selectedFiles[0]?.name ?? "file";
            const upload_response = await handleFileUpload(file_name, selectedFiles[0])
            if (upload_response.status === 200) {
                const save_response = await extractionFileSave(upload_response.fileName, upload_response.fileUrl, upload_response.fileId, doc_type_id);
                if (save_response.status === 200) {
                    const fileResponseId = save_response?.data?.id ?? null;
                    handleExtractionSteps('is_first_doc_uploaded')
                    setFileId(fileResponseId)
                    setModalData(modalDetails)
                    setSelectedFiles([])
                    setPreviewMode(MODES.TRAIN)
                }
            }

        } catch (error) {
            console.log(error)
        } finally {
            setCreatingDocType(false);
        }
    }

    const launchSchemaBuilder = async () => {
        setLoading(true);
        try {
            const token = await getAgentApiToken();
            if (token) {
                setAgentTokenId(token);
                setPreviewMode(MODES.SCHEMA);
            } else {
                tostAlert('Unable to fetch token for schema builder', 'error')
            }
        } catch (error) {
            console.log(error)
            tostAlert('Unable to fetch token for schema builder', 'error')
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        const handleMessage = (event) => {
            if (event.origin !== schema_builder_plugin_url) return;

            if (event.data?.type === "LAUNCH_TRAINING") {
                const extraction_file_id = event.data?.payload?.extraction_file_id ?? null;
                const extraction_document_type_id = event.data?.payload?.extraction_document_type_id ?? null;
                const extraction_document_type = event.data?.payload?.extraction_document_type ?? null;
                if (extraction_file_id && extraction_document_type_id && extraction_document_type) {
                    getDocumentTypes()
                    getAllData()
                    setFileId(extraction_file_id)
                    setDocumentTypeId(extraction_document_type_id)
                    setDocumentType(extraction_document_type)
                    setPreviewMode(MODES.TRAIN)
                }
            }
        };
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, []);

    const showMainLoading = loading || creatingDocType;

    return (
        <div style={styles.mainContainer}>

            {previewMode === MODES.TRAIN && documentTypeId && documentType && fileId &&
                <ExtractionTraining
                    document_type_id={documentTypeId}
                    document_type={documentType}
                    file_id={fileId}
                    setPreviewMode={setPreviewMode}
                />
            }

            {(previewMode === MODES.SCHEMA && agentTokenId) &&
                <SchemaBuilder
                    setPreviewMode={setPreviewMode}
                    agentTokenId={agentTokenId}
                    isVisible={previewMode === MODES.SCHEMA}
                />
            }

            {previewMode === MODES.DOCUMENT &&
                <Box sx={styles.cardContainer}>

                    {showMainLoading && <LoadBar />}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={styles.mainHeadingText}>Select a Document Type</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>

                            <Button
                                onClick={() => launchSchemaBuilder()}
                                disabled={creatingDocType}
                                sx={{
                                    backgroundColor: '#0B51C5',
                                    color: '#ffffff',
                                    borderRadius: '20px',
                                    width: '100px',
                                    opacity: creatingDocType ? 0.6 : 1
                                }}>
                                <Typography sx={{ fontSize: '14px', fontWeight: '600' }}>
                                    New
                                </Typography>
                            </Button>
                            <Button
                                onClick={() => navigate('/document-types')}
                                disabled={creatingDocType}
                                sx={{
                                    backgroundColor: '#DEDEDE',
                                    borderRadius: '20px',
                                    width: '100px',
                                    opacity: creatingDocType ? 0.6 : 1
                                }}>
                                <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#000000' }}>
                                    CANCEL
                                </Typography>
                            </Button>

                        </Box>
                    </Box>

                    {(!loading && (extractionDocumentTypes.all || [])?.length === 0) &&
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Typography>No Documents Available</Typography>
                        </Box>
                    }

                    {loading
                        ?
                        (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress />
                        </Box>)
                        : (<Box>

                            {(extractionDocumentTypes.coaTypes || [])?.length > 0 &&
                                (<Box marginTop="30px">
                                    <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
                                        COA Documents
                                    </Typography>

                                    <Box
                                        display="grid"
                                        gridTemplateColumns="repeat(auto-fit, minmax(250px, 250px))"
                                        gap="20px"
                                        marginTop="10px"
                                        justifyContent="start"
                                    >
                                        {(extractionDocumentTypes.coaTypes || [])?.map((item, index) => (
                                            <Box
                                                key={index}
                                                onClick={() => !creatingDocType && handleModalOpen(item)}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    borderRadius: '10px',
                                                    padding: '10px',
                                                    border: '1px solid #DEDEDE',
                                                    cursor: creatingDocType ? 'not-allowed' : 'pointer',
                                                    width: '250px',
                                                    opacity: creatingDocType ? 0.6 : 1,
                                                    transition: 'background-color 0.2s, color 0.2s',
                                                    "&:hover": creatingDocType
                                                        ? {}
                                                        : {
                                                            backgroundColor: '#0B51C5',
                                                            color: '#ffffff',
                                                            "& svg": { stroke: '#ffffff' },
                                                        },
                                                }}
                                            >
                                                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                    {item?.payload?.name}
                                                </Typography>

                                                <Box sx={{ width: 18, height: 18, marginLeft: '10px' }}>
                                                    <ReactSVG
                                                        src={IconSvg.resultCorrectIcon}
                                                        beforeInjection={(svg) => {
                                                            svg.setAttribute('style', 'width: 18px; height:18px; display: block;');
                                                            svg.setAttribute('stroke', '#0B51C5');
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>)
                            }

                            {(extractionDocumentTypes.certificatesTypes || [])?.length > 0 &&
                                (<Box marginTop="30px">
                                    <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
                                        Certificates
                                    </Typography>

                                    <Box
                                        display="grid"
                                        gridTemplateColumns="repeat(auto-fit, minmax(250px, 250px))"
                                        gap="20px"
                                        marginTop="10px"
                                        justifyContent="start"
                                    >
                                        {(extractionDocumentTypes.certificatesTypes || [])?.map((item, index) => (
                                            <Box
                                                key={index}
                                                onClick={() => !creatingDocType && handleModalOpen(item)}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    borderRadius: '10px',
                                                    padding: '10px',
                                                    border: '1px solid #DEDEDE',
                                                    cursor: creatingDocType ? 'not-allowed' : 'pointer',
                                                    width: '250px',
                                                    opacity: creatingDocType ? 0.6 : 1,
                                                    transition: 'background-color 0.2s, color 0.2s',
                                                    "&:hover": creatingDocType
                                                        ? {}
                                                        : {
                                                            backgroundColor: '#0B51C5',
                                                            color: '#ffffff',
                                                            "& svg": { stroke: '#ffffff' },
                                                        },
                                                }}
                                            >
                                                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                    {item?.payload?.name}
                                                </Typography>

                                                <Box sx={{ width: 18, height: 18, marginLeft: '10px' }}>
                                                    <ReactSVG
                                                        src={IconSvg.resultCorrectIcon}
                                                        beforeInjection={(svg) => {
                                                            svg.setAttribute('style', 'width: 18px; height:18px; display: block;');
                                                            svg.setAttribute('stroke', '#0B51C5');
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>)
                            }

                            {(extractionDocumentTypes.procurementDocument || [])?.length > 0 && (<Box marginTop="30px">
                                <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
                                    Invoices, POs & Remittances Document
                                </Typography>

                                <Box
                                    display="grid"
                                    gridTemplateColumns="repeat(auto-fit, minmax(250px, 250px))"
                                    gap="20px"
                                    marginTop="10px"
                                    justifyContent="start"
                                >
                                    {(extractionDocumentTypes.procurementDocument || [])?.map((item, index) => (
                                        <Box
                                            key={index}
                                            onClick={() => !creatingDocType && handleModalOpen(item)}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                borderRadius: '10px',
                                                padding: '10px',
                                                border: '1px solid #DEDEDE',
                                                cursor: creatingDocType ? 'not-allowed' : 'pointer',
                                                width: '250px',
                                                opacity: creatingDocType ? 0.6 : 1,
                                                transition: 'background-color 0.2s, color 0.2s',
                                                "&:hover": creatingDocType
                                                    ? {}
                                                    : {
                                                        backgroundColor: '#0B51C5',
                                                        color: '#ffffff',
                                                        "& svg": { stroke: '#ffffff' },
                                                    },
                                            }}
                                        >
                                            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                {item?.payload?.name}
                                            </Typography>

                                            <Box sx={{ width: 18, height: 18, marginLeft: '10px' }}>
                                                <ReactSVG
                                                    src={IconSvg.resultCorrectIcon}
                                                    beforeInjection={(svg) => {
                                                        svg.setAttribute('style', 'width: 18px; height:18px; display: block;');
                                                        svg.setAttribute('stroke', '#0B51C5');
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>)}

                            {(extractionDocumentTypes.incomeDocument || [])?.length > 0 && (<Box marginTop="30px">
                                <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
                                    Income
                                </Typography>

                                <Box
                                    display="grid"
                                    gridTemplateColumns="repeat(auto-fit, minmax(250px, 250px))"
                                    gap="20px"
                                    marginTop="10px"
                                    justifyContent="start"
                                >
                                    {(extractionDocumentTypes.incomeDocument || [])?.map((item, index) => (
                                        <Box
                                            key={index}
                                            onClick={() => !creatingDocType && handleModalOpen(item)}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                borderRadius: '10px',
                                                padding: '10px',
                                                border: '1px solid #DEDEDE',
                                                cursor: creatingDocType ? 'not-allowed' : 'pointer',
                                                width: '250px',
                                                opacity: creatingDocType ? 0.6 : 1,
                                                transition: 'background-color 0.2s, color 0.2s',
                                                "&:hover": creatingDocType
                                                    ? {}
                                                    : {
                                                        backgroundColor: '#0B51C5',
                                                        color: '#ffffff',
                                                        "& svg": { stroke: '#ffffff' },
                                                    },
                                            }}
                                        >
                                            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                {item?.payload?.name}
                                            </Typography>

                                            <Box sx={{ width: 18, height: 18, marginLeft: '10px' }}>
                                                <ReactSVG
                                                    src={IconSvg.resultCorrectIcon}
                                                    beforeInjection={(svg) => {
                                                        svg.setAttribute('style', 'width: 18px; height:18px; display: block;');
                                                        svg.setAttribute('stroke', '#0B51C5');
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>)}

                            {(extractionDocumentTypes.foodSafetyTypes || [])?.length > 0 &&
                                (<Box marginTop="30px">
                                    <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
                                        Food Safety
                                    </Typography>

                                    <Box
                                        display="grid"
                                        gridTemplateColumns="repeat(auto-fit, minmax(250px, 250px))"
                                        gap="20px"
                                        marginTop="10px"
                                        justifyContent="start"
                                    >
                                        {(extractionDocumentTypes.foodSafetyTypes || [])?.map((item, index) => (
                                            <Box
                                                key={index}
                                                onClick={() => !creatingDocType && handleModalOpen(item)}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    borderRadius: '10px',
                                                    padding: '10px',
                                                    border: '1px solid #DEDEDE',
                                                    cursor: creatingDocType ? 'not-allowed' : 'pointer',
                                                    width: '250px',
                                                    opacity: creatingDocType ? 0.6 : 1,
                                                    transition: 'background-color 0.2s, color 0.2s',
                                                    "&:hover": creatingDocType
                                                        ? {}
                                                        : {
                                                            backgroundColor: '#0B51C5',
                                                            color: '#ffffff',
                                                            "& svg": { stroke: '#ffffff' },
                                                        },
                                                }}
                                            >
                                                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                    {item?.payload?.name}
                                                </Typography>

                                                <Box sx={{ width: 18, height: 18, marginLeft: '10px' }}>
                                                    <ReactSVG
                                                        src={IconSvg.resultCorrectIcon}
                                                        beforeInjection={(svg) => {
                                                            svg.setAttribute('style', 'width: 18px; height:18px; display: block;');
                                                            svg.setAttribute('stroke', '#0B51C5');
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>)
                            }

                            {(extractionDocumentTypes.logisticsAndShippingTypes || [])?.length > 0 &&
                                (<Box marginTop="30px">
                                    <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
                                        Logistics & Shipping
                                    </Typography>

                                    <Box
                                        display="grid"
                                        gridTemplateColumns="repeat(auto-fit, minmax(250px, 250px))"
                                        gap="20px"
                                        marginTop="10px"
                                        justifyContent="start"
                                    >
                                        {(extractionDocumentTypes.logisticsAndShippingTypes || [])?.map((item, index) => (
                                            <Box
                                                key={index}
                                                onClick={() => !creatingDocType && handleModalOpen(item)}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    borderRadius: '10px',
                                                    padding: '10px',
                                                    border: '1px solid #DEDEDE',
                                                    cursor: creatingDocType ? 'not-allowed' : 'pointer',
                                                    width: '250px',
                                                    opacity: creatingDocType ? 0.6 : 1,
                                                    transition: 'background-color 0.2s, color 0.2s',
                                                    "&:hover": creatingDocType
                                                        ? {}
                                                        : {
                                                            backgroundColor: '#0B51C5',
                                                            color: '#ffffff',
                                                            "& svg": { stroke: '#ffffff' },
                                                        },
                                                }}
                                            >
                                                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                    {item?.payload?.name}
                                                </Typography>

                                                <Box sx={{ width: 18, height: 18, marginLeft: '10px' }}>
                                                    <ReactSVG
                                                        src={IconSvg.resultCorrectIcon}
                                                        beforeInjection={(svg) => {
                                                            svg.setAttribute('style', 'width: 18px; height:18px; display: block;');
                                                            svg.setAttribute('stroke', '#0B51C5');
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>)
                            }


                            {(extractionDocumentTypes.tradeDocumentTypes || [])?.length > 0 &&
                                (<Box marginTop="30px">
                                    <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
                                        Trade Document
                                    </Typography>

                                    <Box
                                        display="grid"
                                        gridTemplateColumns="repeat(auto-fit, minmax(250px, 250px))"
                                        gap="20px"
                                        marginTop="10px"
                                        justifyContent="start"
                                    >
                                        {(extractionDocumentTypes.tradeDocumentTypes || [])?.map((item, index) => (
                                            <Box
                                                key={index}
                                                onClick={() => !creatingDocType && handleModalOpen(item)}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    borderRadius: '10px',
                                                    padding: '10px',
                                                    border: '1px solid #DEDEDE',
                                                    cursor: creatingDocType ? 'not-allowed' : 'pointer',
                                                    width: '250px',
                                                    opacity: creatingDocType ? 0.6 : 1,
                                                    transition: 'background-color 0.2s, color 0.2s',
                                                    "&:hover": creatingDocType
                                                        ? {}
                                                        : {
                                                            backgroundColor: '#0B51C5',
                                                            color: '#ffffff',
                                                            "& svg": { stroke: '#ffffff' },
                                                        },
                                                }}
                                            >
                                                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                    {item?.payload?.name}
                                                </Typography>

                                                <Box sx={{ width: 18, height: 18, marginLeft: '10px' }}>
                                                    <ReactSVG
                                                        src={IconSvg.resultCorrectIcon}
                                                        beforeInjection={(svg) => {
                                                            svg.setAttribute('style', 'width: 18px; height:18px; display: block;');
                                                            svg.setAttribute('stroke', '#0B51C5');
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                                )
                            }

                            {(extractionDocumentTypes.customsDocument || [])?.length > 0 && (<Box marginTop="30px">
                                <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
                                    Customs Document
                                </Typography>

                                <Box
                                    display="grid"
                                    gridTemplateColumns="repeat(auto-fit, minmax(250px, 250px))"
                                    gap="20px"
                                    marginTop="10px"
                                    justifyContent="start"
                                >
                                    {(extractionDocumentTypes.customsDocument || [])?.map((item, index) => (
                                        <Box
                                            key={index}
                                            onClick={() => !creatingDocType && handleModalOpen(item)}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                borderRadius: '10px',
                                                padding: '10px',
                                                border: '1px solid #DEDEDE',
                                                cursor: creatingDocType ? 'not-allowed' : 'pointer',
                                                width: '250px',
                                                opacity: creatingDocType ? 0.6 : 1,
                                                transition: 'background-color 0.2s, color 0.2s',
                                                "&:hover": creatingDocType
                                                    ? {}
                                                    : {
                                                        backgroundColor: '#0B51C5',
                                                        color: '#ffffff',
                                                        "& svg": { stroke: '#ffffff' },
                                                    },
                                            }}
                                        >
                                            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                {item?.payload?.name}
                                            </Typography>

                                            <Box sx={{ width: 18, height: 18, marginLeft: '10px' }}>
                                                <ReactSVG
                                                    src={IconSvg.resultCorrectIcon}
                                                    beforeInjection={(svg) => {
                                                        svg.setAttribute('style', 'width: 18px; height:18px; display: block;');
                                                        svg.setAttribute('stroke', '#0B51C5');
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>)}

                            {(extractionDocumentTypes.customTypes || [])?.length > 0 &&
                                (<Box marginTop="30px">
                                    <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
                                        User Documents
                                    </Typography>

                                    <Box
                                        display="grid"
                                        gridTemplateColumns="repeat(auto-fit, minmax(250px, 250px))"
                                        gap="20px"
                                        marginTop="10px"
                                        justifyContent="start"
                                    >
                                        {(extractionDocumentTypes.customTypes || [])?.map((item, index) => (
                                            <Box
                                                key={index}
                                                onClick={() => !creatingDocType && handleModalOpen(item)}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    borderRadius: '10px',
                                                    padding: '10px',
                                                    border: '1px solid #DEDEDE',
                                                    cursor: creatingDocType ? 'not-allowed' : 'pointer',
                                                    width: '250px',
                                                    opacity: creatingDocType ? 0.6 : 1,
                                                    transition: 'background-color 0.2s, color 0.2s',
                                                    "&:hover": creatingDocType
                                                        ? {}
                                                        : {
                                                            backgroundColor: '#0B51C5',
                                                            color: '#ffffff',
                                                            "& svg": { stroke: '#ffffff' },
                                                        },
                                                }}
                                            >
                                                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                    {item?.payload?.name}
                                                </Typography>

                                                <Box sx={{ width: 18, height: 18, marginLeft: '10px' }}>
                                                    <ReactSVG
                                                        src={IconSvg.resultCorrectIcon}
                                                        beforeInjection={(svg) => {
                                                            svg.setAttribute('style', 'width: 18px; height:18px; display: block;');
                                                            svg.setAttribute('stroke', '#0B51C5');
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>)
                            }

                        </Box>)
                    }

                    <Modal open={modalData.modalstatus}
                        onClose={!creatingDocType ? handleModalClose : undefined}
                        aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                        <Box sx={mainStyles.modalcontainer}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography sx={{ fontSize: '16px', fontWeight: '600' }}>
                                    {creatingDocType ? 'Creating Document Type...' : 'Create Document Type'}
                                </Typography>
                                {!creatingDocType && (
                                    <IconButton onClick={handleModalClose}>
                                        <Close />
                                    </IconButton>
                                )}
                            </Box>
                            <Box marginTop={'15px'}>
                                <Typography sx={{ fontSize: '14px', fontWeight: '600' }}>Document Type</Typography>
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', borderRadius: '10px',
                                    backgroundColor: '#F3F5F7', padding: '12px', marginTop: '10px'
                                }}>
                                    <Typography sx={{ fontSize: '14px', fontWeight: '500', marginRight: '10px' }}>{modalData.document_name}</Typography>
                                    <ReactSVG
                                        src={IconSvg.resultCorrectIcon}
                                        beforeInjection={(svg) => {
                                            svg.setAttribute('style', 'width: 18px; height:18px; display: block;');
                                            svg.setAttribute('stroke', '#0B51C5');
                                        }}
                                    />
                                </Box>
                            </Box>
                            <Box marginTop={'20px'}>
                                <Typography sx={{ fontSize: '14px', fontWeight: '600' }}>Name</Typography>
                                <TextField
                                    size='small'
                                    fullWidth
                                    disabled={creatingDocType}
                                    value={modalData.document_type_name}
                                    onChange={(e) => setModalData({ ...modalData, document_type_name: e.target.value })}
                                />
                                {formErrors.document_type_name && <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'red' }}>{formErrors.document_type_name}</Typography>}
                            </Box>
                            <Box sx={{
                                marginTop: '20px',
                                padding: '20px',
                                textAlign: 'center',
                                cursor: creatingDocType ? 'not-allowed' : 'pointer',
                                backgroundColor: '#F3F5F7',
                                borderRadius: '20px',
                                opacity: creatingDocType ? 0.6 : 1
                            }}>
                                <Box
                                    onDrop={!creatingDocType ? handleFileDrop : undefined}
                                    onDragOver={!creatingDocType ? (e) => e.preventDefault() : undefined}
                                >
                                    <input
                                        type="file"
                                        accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg"
                                        // accept=".pdf,.docx,.doc,.xls,.xlsx,.csv"
                                        disabled={creatingDocType}
                                        onChange={handleFileChange}
                                        style={{ display: 'none', cursor: 'pointer' }}
                                        id="fileInput"
                                    />
                                    <label
                                        id='fileInput'
                                        style={{ cursor: creatingDocType ? 'not-allowed' : 'pointer' }}
                                        htmlFor="fileInput">
                                        {selectedFiles.length === 0 && (
                                            <Box>
                                                <Typography sx={styles.mainHeadingText}>
                                                    Drag and drop files or&nbsp;
                                                    <span style={{
                                                        color: creatingDocType ? '#999' : '#0B51C5',
                                                        cursor: creatingDocType ? 'not-allowed' : 'pointer'
                                                    }}>
                                                        click to upload
                                                    </span>
                                                </Typography>
                                                <Typography sx={styles.paraText}>
                                                    <p><strong>Accepted file formats:</strong> PDF, Word, Excel, PNG, JPG, JPEG</p>
                                                </Typography>
                                            </Box>
                                        )}
                                        <IconButton component="span" disabled={creatingDocType}>
                                            <CloudUpload style={{
                                                color: creatingDocType ? "#999" : "black",
                                                width: "32px",
                                                height: "32px"
                                            }} />
                                        </IconButton>
                                    </label>
                                </Box>
                                <Box display={'flex'} justifyContent={'center'} alignItems={'center'} flexWrap="wrap" >
                                    {
                                        selectedFiles.length > 0 && selectedFiles.map((file, index) => (
                                            < Box key={index} display={'flex'} justifyContent={'center'} alignItems={'center'} alignContent={'center'} textAlign={'center'} padding={'3px 15px 3px 15px'} margin={'5px'} backgroundColor={'#FFFFFF'} borderRadius={'20px'}>
                                                <Typography sx={styles.paraText}>{file.name}</Typography>
                                                {!creatingDocType && (
                                                    <Box marginLeft={'10px'} onClick={() => handleFileRemove(index)}>
                                                        <ReactSVG src={IconSvg.deleteIcon} className='delete_icon' />
                                                    </Box>
                                                )}
                                            </Box>
                                        ))
                                    }
                                </Box>
                            </Box>
                            <Box sx={{ marginTop: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <Button
                                    onClick={handleUploadTrain}
                                    disabled={creatingDocType}
                                    sx={{
                                        ...styles.uploadBtn,
                                        opacity: creatingDocType ? 0.6 : 1
                                    }}
                                >
                                    <Typography sx={styles.btnText}>
                                        {creatingDocType ? 'Processing...' : 'Upload and Train'}
                                    </Typography>
                                </Button>
                            </Box>
                        </Box>
                    </Modal>

                </Box>
            }

        </div >

    )
}

export default AddDocumentTypes;